/**
 * NoopAnalyticsService - No-op implementation for export mode
 *
 * All methods do nothing. Used when Firebase credentials are not provided.
 * This allows exported packages to use the same viewer code without
 * sending any analytics data.
 */

import type { IAnalyticsService } from '../interfaces';
import { Logger } from '../../utils/Logger';

const logger = Logger.getLogger('NoopAnalyticsService');

export class NoopAnalyticsService implements IAnalyticsService {
  private currentStageIndex: number = 0;
  private isInVR: boolean = false;
  private sessionId: string = `noop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Context - no-op
  setContext(_sequenceId: string | null, _userId: string | null): void {}
  setEnabled(_enabled: boolean): void {}

  // Session tracking - no-op
  trackSessionStarted(): void {}
  trackSessionEnded(): void {}

  // Sequence tracking - no-op
  trackSequenceLoaded(
    _sequenceName: string,
    _isPublic: boolean,
    _hasPassword: boolean,
    _organizationId: string | null
  ): void {}
  trackSequenceViewStarted(): void {}
  trackSequenceViewCompleted(): void {}

  // Stage tracking - track index for utility methods
  trackStageNavigation(
    _fromStage: number,
    toStage: number,
    _method: 'next' | 'previous' | 'jump' | 'selector',
    _totalStages: number
  ): void {
    this.currentStageIndex = toStage;
  }
  trackStageViewStarted(
    stageIndex: number,
    _stageName: string | undefined,
    _hasVideo: boolean,
    _hasModels: boolean,
    _hasPlanes: boolean,
    _hasAudio: boolean
  ): void {
    this.currentStageIndex = stageIndex;
  }
  trackStageViewEnded(_stageIndex: number, _stageName: string | undefined): void {}
  trackStageSelectorOpened(_mode: '2D' | 'VR', _stageIndex: number): void {}
  trackStageSelectorClosed(_mode: '2D' | 'VR', _stageIndex: number): void {}

  // VR tracking - track state for utility methods
  trackVRSessionStarted(): void {
    this.isInVR = true;
  }
  trackVRSessionEnded(): void {
    this.isInVR = false;
  }
  trackVRStateChanged(state: 'entering' | 'in' | 'exiting' | 'not_in'): void {
    this.isInVR = state === 'in' || state === 'entering';
  }
  trackVRControllerConnected(
    _handedness: 'left' | 'right' | 'unknown',
    _controllerType?: string
  ): void {}

  // Video tracking - no-op
  trackVideoPlay(_stageIndex: number, _videoUrl?: string): void {}
  trackVideoPause(_stageIndex: number, _currentTime?: number): void {}
  trackVideoComplete(_stageIndex: number, _duration?: number): void {}
  trackVideoError(_stageIndex: number, _errorMessage: string): void {}

  // Interaction tracking - no-op
  trackFOVZoom(_newFOV: number, _previousFOV: number, _method: 'wheel' | 'pinch'): void {}
  trackControllerButton(
    _button: 'trigger-left' | 'trigger-right' | 'a' | 'b' | 'x' | 'y',
    _action: string,
    _stageIndex?: number
  ): void {}

  // Performance tracking - no-op
  trackPerformance(_fps: number, _stageIndex?: number): void {}
  trackStageLoadTime(_stageIndex: number, _loadTime: number): void {}
  trackMediaLoadTime(
    _resourceType: 'model' | 'video' | 'image',
    _resourceUrl: string,
    _loadTime: number,
    _stageIndex?: number
  ): void {}

  // Error tracking - log to console for debugging
  trackError(
    errorType: string,
    errorMessage: string,
    stack?: string,
    _stageIndex?: number
  ): void {
    // Still log errors to console for debugging, even in export mode
    logger.error(`${errorType}: ${errorMessage}`, stack || '');
  }

  // Utility methods
  getCurrentStageIndex(): number {
    return this.currentStageIndex;
  }

  getIsInVR(): boolean {
    return this.isInVR;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  dispose(): void {
    // Nothing to dispose
  }
}
